import React, { useCallback, useEffect, useState } from 'react';
import { Container } from "../../../components/Container/Container";
import { DialogActions } from "../../../components/Dialog/DialogActions";
import { DialogButton } from "../../../components/Dialog/DialogButton";
import { useAuth } from "../../../hooks/useAuth";
import { useDeclarativeLoading } from "../../../hooks/useLoading";
import { useTranslation } from "../../../hooks/useTranslation";
import { useUseCase } from "../../../hooks/useUseCase";
import { makeStyles } from "../../../styles/index";
import { SelectedWorks } from "../components/SelectedWorks";
import { defaultFilterConditionObject, FilterCondition } from "../models/Work/FilterCondition";
import { Work } from "../models/Work/Work";
import { WorkInvalidCourseDialog } from "./components/WorkInvalidCourseDialog";
import { WorkSelectDialogContent } from "./components/WorkSelectDialogContent";
import { WorkSelectDialogToolbar } from "./components/WorkSelectDialogToolbar";
import { WorkUnavailableCourseDialog } from "./components/WorkUnavailableCourseDialog";
import { WorkUnavailableDialog } from "./components/WorkUnavailableDialog";
import { ChangeFilterUseCase } from "./usecase/ChangeFilterUseCase";
import { ChangePaginationUseCase } from "./usecase/ChangePaginationUseCase";
import { CloseWorkSelectDialogUseCase } from "./usecase/CloseWorkSelectDialogUseCase";
import { OpenWorkSelectDialogUseCase } from "./usecase/OpenWorkSelectDialogUseCase";
import { SelectWorkUseCase } from "./usecase/SelectWorkUseCase";
import { UnselectWorkUseCase } from "./usecase/UnselectWorkUseCase";
import { initialWorkTabState } from "./WorkTabState";
var useStyles = makeStyles(function () { return ({
    root: {},
    container: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        overflow: 'hidden',
        maxWidth: 'none',
        padding: 0,
    },
    tab: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'flex-end',
        minHeight: 64,
        position: 'relative',
    },
    toolbar: {
        position: 'absolute',
        top: 36,
        right: 0,
    },
    actions: {
        position: 'relative',
    },
}); }, { name: 'SnWorkTab' });
/**
 * 「作成したワーク」タブ
 */
export var WorkTab = function (_a) {
    var open = _a.open, _b = _a.defaultWork, defaultWork = _b === void 0 ? [] : _b, maxSelectableWorkNumber = _a.maxSelectableWorkNumber, schoolType = _a.schoolType, onSelect = _a.onSelect, onCancel = _a.onCancel;
    var user = useAuth().user;
    var _c = useUseCase(initialWorkTabState), state = _c.state, usecase = _c.usecase;
    var _d = useState(false), hasUnavailableCourseWork = _d[0], setHasUnavailableCourseWork = _d[1];
    var _e = useState(false), hasInvalidCourseWork = _e[0], setHasInvalidCourseWork = _e[1];
    var _f = useState(false), hasUnavailableWork = _f[0], setHasUnavailableWork = _f[1];
    var t = useTranslation().t;
    var classes = useStyles();
    useDeclarativeLoading('loading', state.isLoading);
    useEffect(function () {
        if (!user)
            return;
        open
            ? usecase(new OpenWorkSelectDialogUseCase()).execute({
                defaultWork: defaultWork.map(function (_defaultWork) { return Work.fromCallback(_defaultWork); }),
                userId: user.userId,
            })
            : usecase(new CloseWorkSelectDialogUseCase()).execute();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, user]);
    var handleFilterChange = useCallback(function (filterCondition) {
        if (!user)
            return;
        usecase(new ChangeFilterUseCase()).execute({ filterCondition: filterCondition, userId: user.userId });
    }, [usecase, user]);
    var handleFilterClear = useCallback(function () {
        if (!user)
            return;
        usecase(new ChangeFilterUseCase()).execute({
            filterCondition: new FilterCondition(defaultFilterConditionObject),
            userId: user.userId,
        });
    }, [usecase, user]);
    var handleChangePagination = useCallback(function (nextPage) {
        if (!user)
            return;
        usecase(new ChangePaginationUseCase()).execute({
            nextPage: nextPage,
            filterCondition: state.filterCondition,
            sortCondition: state.sortCondition,
            userId: user.userId,
        });
    }, [state.filterCondition, state.sortCondition, usecase, user]);
    var handleSelectWork = useCallback(function (work, selected) {
        selected
            ? usecase(new UnselectWorkUseCase()).execute({ work: work })
            : usecase(new SelectWorkUseCase()).execute({ work: work });
    }, [usecase]);
    var handleCancel = useCallback(function () {
        onCancel();
    }, [onCancel]);
    var handleSelect = useCallback(function () {
        if (state.selectedWorks.works.length === 0)
            return;
        // 教科書出版社設定由来で配付できないワークがないかどうかをチェック
        if (state.selectedUnavailableCourseWorkNames.length > 0) {
            setHasUnavailableCourseWork(true);
            return;
        }
        // 教材改訂が理由で配付できないワークがないかどうかをチェック
        if (state.selectedInvalidCourseWorkNames.length > 0) {
            setHasInvalidCourseWork(true);
            return;
        }
        // 教材の削除に伴う不正状態によって配付できないワークがないかどうかチェック
        if (state.selectedUnavailableWorkNames.length > 0) {
            setHasUnavailableWork(true);
            return;
        }
        onSelect(state.selectedWorks.works.map(function (work) { return work.getWorkForCallback; }));
    }, [
        state.selectedWorks.works,
        state.selectedUnavailableCourseWorkNames.length,
        state.selectedInvalidCourseWorkNames.length,
        state.selectedUnavailableWorkNames.length,
        onSelect,
    ]);
    var handleCloseWorkUnavailableCourseDialog = function () {
        setHasUnavailableCourseWork(false);
    };
    var handleCloseWorkInvalidCourseDialog = function () {
        setHasInvalidCourseWork(false);
    };
    var handleCloseWorkUnavailableDialog = function () {
        setHasUnavailableWork(false);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classes.toolbar },
            React.createElement(WorkSelectDialogToolbar, { onFilterChange: handleFilterChange, onFilterClear: handleFilterClear, filterCondition: state.filterCondition, isFilterEmpty: state.filterCondition.isEmpty, pagination: state.pagination, schoolType: schoolType, onChangePagination: handleChangePagination })),
        React.createElement(Container, { className: classes.container },
            React.createElement(WorkSelectDialogContent, { works: state.works, onSelectWork: handleSelectWork, selectedWorkId: state.selectedWorkIds, selectable: state.selectedWorkIds.length < maxSelectableWorkNumber, sortCondition: state.sortCondition, onChangePagination: handleChangePagination, isLoading: state.isLoading, hasError: state.hasError })),
        React.createElement(DialogActions, { className: classes.actions },
            React.createElement(SelectedWorks, { maxCount: maxSelectableWorkNumber, selectedCount: state.selectedWorkIds.length }),
            React.createElement(DialogButton, { onClick: handleCancel }, t('キャンセル')),
            React.createElement(DialogButton, { fill: true, onClick: handleSelect, disabled: state.selectedWorks.works.length === 0 }, t('選ぶ'))),
        React.createElement(WorkUnavailableCourseDialog, { open: hasUnavailableCourseWork, workNames: state.selectedUnavailableCourseWorkNames, onClose: handleCloseWorkUnavailableCourseDialog }),
        React.createElement(WorkInvalidCourseDialog, { open: hasInvalidCourseWork, workNames: state.selectedInvalidCourseWorkNames, onClose: handleCloseWorkInvalidCourseDialog }),
        React.createElement(WorkUnavailableDialog, { open: hasUnavailableWork, workNames: state.selectedUnavailableWorkNames, onClose: handleCloseWorkUnavailableDialog })));
};
